// ═══════════════════════════════════════════════════════════════════════════
// AI Command Schemas for the MATCHPROP family (CC-2c PR-1)
// ═══════════════════════════════════════════════════════════════════════════
//
// Zod schemas describing the JSON the AI produces when it wants to PAINT the
// properties of one source entity onto one or more target entities — the
// MATCHPROP (Match Properties / Property Painter) slice of ТЗ §10.1. As with
// every other AI domain, each schema here is a plain DATA contract — it does NOT
// mutate anything. Translation to internal `EditorCommand`s happens in
// `matchpropDispatcher.ts`, and the reducer pipeline (the same path the UI /
// command line / CLI `MATCHPROP` dispatch through — `propertyReducer.ts`) is the
// only place that ever writes to the document. This enforces the CLAUDE.md rules:
//   "Properties, CHPROP, MATCHPROP и OSNAP — это системные механизмы, а не
//    факультативные UI-надстройки." +
//   "AI drafting … может только предлагать и исполнять команды через безопасный
//    контур."
//
// SCOPE — the single MATCHPROP command, which ТЗ §10.1 marks `aiCallable: yes`
// (its registry descriptor is `aiCallable: true`, category "system"). MATCHPROP
// is a TWO-STEP mechanism in the model: pick a SOURCE entity
// (`matchprop/set-source`) then PAINT its properties onto a batch of TARGETS
// (`matchprop/apply`) with a property-group `mode`. The AI action carries both
// halves so one `MATCH_PROPERTIES` proposal emits BOTH internal commands as an
// atomic pair (the app wraps the returned `EditorCommand[]` in one grouped-undo
// unit, so a single undo reverses the whole paint).
//
// Design notes (mirror drawModifySchemas.ts / viewSpaceSchemas.ts):
//   - The `action: "MATCH_PROPERTIES"` literal discriminator keeps the action
//     DISJOINT from the block / entity / draw-modify / view-space / base-prim
//     domains, so `aiDispatcher.ts` picks the owning domain by `action` alone.
//   - `source` / `targets` / `mode` are derived STRICTLY from how the reducer
//     consumes the two EditorCommands (contracts/src/editor.ts), never invented:
//       • `source`  → `matchprop/set-source.entityId` (the donor id). The
//         contract types it `string | null` (null clears the armed source), but
//         an AI "copy properties from A" ALWAYS names a concrete donor, so the
//         AI shape narrows it to a REQUIRED non-empty id (a null-source paint is
//         a UI-only "disarm", not an AI authoring intent).
//       • `targets` → `matchprop/apply.targetEntityIds` (min-1 batch of ids).
//       • `mode`    → `matchprop/apply.mode` — the REAL MATCHPROP settings model
//         the reducer consumes (`matchpropModeSchema`, the ONE-truth enum from
//         `@dxf-ai-editor/contracts`): "basic" (the always-copied general
//         properties — Color/Layer/Linetype/Linetype Scale/Lineweight/
//         Transparency/Thickness/PlotStyle), "special" (the object-type Special
//         property groups — Dimension/Text/Hatch/Polyline/Viewport/Table/
//         Material/Multileader — from the Match Properties Settings dialog), or
//         "all" (both). The reducer's `matchprop/apply` accepts EXACTLY this
//         three-value enum, so the AI mirrors it verbatim (NOT a boolean, NOT a
//         per-flag structure the reducer can't consume). `mode` is OPTIONAL and
//         DEFAULTS to "all" — AutoCAD's Match Properties copies every applicable
//         property group by default (all Settings-dialog checkboxes ticked), the
//         same default the CLI `MATCHPROP` (no BASIC/SPECIAL keyword) resolves to.

import { z } from "zod";

import { matchpropModeSchema } from "@dxf-ai-editor/contracts";

// ═══════════════════════════════════════════════════════════════════════════
// MATCH_PROPERTIES (MATCHPROP / MA / КОПИРСВ — Property Painter)
// ═══════════════════════════════════════════════════════════════════════════
//
// Emits `matchprop/set-source` (arm the donor) THEN `matchprop/apply` (paint the
// batch) — see `translateMatchProperties`. `source` is the donor entity id;
// `targets` is the non-empty batch of recipient ids; `mode` selects which
// property groups are copied (defaults to "all", AutoCAD parity).
export const matchPropertiesAiSchema = z.object({
  action: z.literal("MATCH_PROPERTIES"),
  source: z.string().min(1),
  targets: z.array(z.string().min(1)).min(1),
  mode: matchpropModeSchema.optional().default("all"),
});
export type MatchPropertiesAiCommand = z.infer<typeof matchPropertiesAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Top-level MATCHPROP AI command discriminated union
// ═══════════════════════════════════════════════════════════════════════════
//
// A discriminated union (single member today) so the domain plugs into the
// combined `aiCommandSchema` the SAME way as every sibling domain (spread of
// `.options`), and so a future MATCHPROP-family action drops in without changing
// the wiring.
export const matchpropAiCommandSchema = z.discriminatedUnion("action", [
  matchPropertiesAiSchema,
]);
export type MatchpropAiCommand = z.infer<typeof matchpropAiCommandSchema>;

// A batch of MATCHPROP commands the AI can emit in one response. The dispatcher
// resolves each in order.
export const matchpropAiCommandBatchSchema = z.array(matchpropAiCommandSchema);
export type MatchpropAiCommandBatch = z.infer<
  typeof matchpropAiCommandBatchSchema
>;

// Convenience: literal action names for the router, guard map, error messages
// and telemetry. Kept in sync with the union by the exhaustive guard Record.
export const MATCHPROP_AI_ACTIONS = ["MATCH_PROPERTIES"] as const;
export type MatchpropAiAction = (typeof MATCHPROP_AI_ACTIONS)[number];

const MATCHPROP_ACTION_SET: ReadonlySet<string> = new Set(MATCHPROP_AI_ACTIONS);

/**
 * Router predicate: does this AI command belong to the MATCHPROP domain?
 *
 * The MATCHPROP, block, entity, draw-modify, view-space and base-prim action
 * literal sets are DISJOINT, so a membership test on `action` is a sound
 * discriminator. The type predicate lets `aiDispatcher.ts` narrow the top-level
 * union to the owning domain without importing the `AiCommand` type here (which
 * would create a module cycle).
 */
export function isMatchpropAiCommand(
  command: { action: string },
): command is MatchpropAiCommand {
  return MATCHPROP_ACTION_SET.has(command.action);
}
